import { useRecoilState } from 'recoil';
import { useSnackbar } from 'fronton-react';
import { setConfigurationIdCookie } from 'pages/common/helpers/set-configuration-id-cookie';
import { SNACKBARS_DICTIONARY, TSnackbarDictionaryKeys } from 'pages/common/components/Snackbar';
import { userProjectConfigurationCookieNames } from 'pages/project-configuration/const';
import { ProjectStatuses, TEditConfigurationGeneralItem } from 'pages/common/types';
import { getDomain } from 'utils/get-domain.helper';
import { deleteCookie, setCookie } from 'utils/cookie.helper';
import { TApi } from 'utils/api';
import { SAVED_CONFIGURATION_LAMINATE_COOKIE } from '../consts/cookies';
import { isProjectsSavedMap } from '../store/projects';
import { ENDPOINTS } from '../consts/endpoints';

type TParams = {
  api: TApi;
  configurationId: string;
  isOpenFromProject?: boolean;
  totalAmount?: number;
  regionId?: string;
  generalItems?: TEditConfigurationGeneralItem[];
  isInitialProjectSaved?: boolean;
};

export const useToggleProjectStatus = ({
  api,
  totalAmount,
  configurationId,
  isOpenFromProject,
  regionId,
  generalItems,
  isInitialProjectSaved = false,
}: TParams) => {
  const [savedProjectMap, setSavedProjectMap] = useRecoilState(isProjectsSavedMap);
  const domain = getDomain();
  const { addSnack } = useSnackbar();
  const isProjectSaved = savedProjectMap?.[configurationId] ?? isInitialProjectSaved;

  const saveProject = async () => {
    try {
      const body = {
        projectStatus: ProjectStatuses.SAVED,
        ...(!isOpenFromProject && {
          regionId,
          approximateTotalAmount: totalAmount,
          generalItems,
        }),
      };

      await api.patch(ENDPOINTS.EDIT_CONFIGURATION(configurationId), body);

      setCookie(SAVED_CONFIGURATION_LAMINATE_COOKIE, 'true', {
        path: '/',
        domain,
        expires: new Date(new Date().getFullYear() + 1, 12, 31),
      });
      deleteCookie(userProjectConfigurationCookieNames.LAMINATE);

      setSavedProjectMap(prevState => ({ ...prevState, [configurationId]: true }));
      addSnack(SNACKBARS_DICTIONARY[TSnackbarDictionaryKeys.ADD_TO_SHOPPING_LIST_SUCCESS]);
    } catch {
      setSavedProjectMap(prevState => ({ ...prevState, [configurationId]: false }));
      addSnack(SNACKBARS_DICTIONARY[TSnackbarDictionaryKeys.ADD_TO_SHOPPING_LIST_ERROR]);
    }
  };

  const unsaveProject = async () => {
    try {
      await api.patch(ENDPOINTS.EDIT_CONFIGURATION(configurationId), {
        projectStatus: ProjectStatuses.NEW,
      });

      if (configurationId) {
        setConfigurationIdCookie({
          projectConfigurationId: configurationId,
          projectConfigurationCookieName: userProjectConfigurationCookieNames.LAMINATE,
        });
      }

      setSavedProjectMap(prevState => ({ ...prevState, [configurationId]: false }));
    } catch {
      setSavedProjectMap(prevState => ({ ...prevState, [configurationId]: true }));
    }
  };

  return {
    isProjectSaved,
    saveProject,
    unsaveProject,
  };
};
